import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, Crown, Zap, Sparkles, Users, Package, 
  TrendingUp, RefreshCw, Search, Filter, MoreVertical,
  CheckCircle, AlertCircle, Clock, Edit, History,
  ChevronDown, X, Loader2, ArrowUpRight, ShoppingBag,
  CreditCard, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_CONFIG = {
  free: {
    name: 'Free',
    icon: Sparkles,
    color: 'from-gray-500 to-gray-600',
    bgColor: 'bg-gray-500/10',
    textColor: 'text-gray-400',
    badgeColor: 'bg-gray-500/20 text-gray-300'
  },
  standard: {
    name: 'Standard',
    icon: Zap,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-400',
    badgeColor: 'bg-blue-500/20 text-blue-300'
  },
  pro: {
    name: 'Pro',
    icon: Crown,
    color: 'from-purple-500 to-pink-600',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400',
    badgeColor: 'bg-purple-500/20 text-purple-300'
  },
  enterprise: {
    name: 'Enterprise',
    icon: Building2,
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-400',
    badgeColor: 'bg-amber-500/20 text-amber-300'
  }
};

export default function SuperAdminBillingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [plans, setPlans] = useState([]);
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [planLogs, setPlanLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    byPlan: {},
    totalOrders: 0,
    totalLeads: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [companiesRes, plansRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/shopify-billing/admin/companies`, { headers }),
        axios.get(`${API_BASE_URL}/api/shopify-billing/plans`)
      ]);

      const companiesData = companiesRes.data.companies || [];
      setCompanies(companiesData);
      setFilteredCompanies(companiesData);
      setPlans(plansRes.data.plans || []);

      // Calculate stats
      const planCounts = {};
      let totalOrders = 0;
      let totalLeads = 0;

      companiesData.forEach(company => {
        const plan = company.plan_type || 'free';
        planCounts[plan] = (planCounts[plan] || 0) + 1;
        totalOrders += company.current_usage?.orders || 0;
        totalLeads += company.current_usage?.leads || 0;
      });

      setStats({
        total: companiesData.length,
        byPlan: planCounts,
        totalOrders,
        totalLeads
      });

    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let filtered = companies;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.shop_domain?.toLowerCase().includes(query) ||
        c.id?.toLowerCase().includes(query)
      );
    }

    // Plan filter
    if (planFilter !== 'all') {
      filtered = filtered.filter(c => (c.plan_type || 'free') === planFilter);
    }

    setFilteredCompanies(filtered);
  }, [searchQuery, planFilter, companies]);

  const handleEditPlan = (company) => {
    setSelectedCompany(company);
    setNewPlan(company.plan_type || 'free');
    setUpdateReason('');
    setEditModalOpen(true);
  };

  const handleUpdatePlan = async () => {
    if (!selectedCompany || !newPlan) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/shopify-billing/admin/update-plan`,
        {
          company_id: selectedCompany.id,
          plan_type: newPlan,
          reason: updateReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Plan updated to ${PLAN_CONFIG[newPlan]?.name || newPlan}`);
      setEditModalOpen(false);
      fetchData();
    } catch (err) {
      console.error('Failed to update plan:', err);
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetUsage = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/shopify-billing/admin/reset-usage?company_id=${companyId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Usage reset successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to reset usage');
    }
  };

  const handleViewHistory = async (company) => {
    setSelectedCompany(company);
    setHistoryModalOpen(true);
    setLogsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/shopify-billing/admin/plan-logs?company_id=${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlanLogs(response.data.logs || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLogsLoading(false);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (limit >= 999999) return 0; // Unlimited
    return Math.min(Math.round((used / limit) * 100), 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const formatNumber = (num) => {
    if (num >= 999999) return '∞';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-wt-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg p-6" data-testid="super-admin-billing">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Billing Management
          </h1>
          <p className="text-gray-400">
            Manage company plans and monitor usage across all tenants
          </p>
        </div>
        <Button
          onClick={() => navigate('/super-admin/stripe-settings')}
          className="bg-wt-primary"
          data-testid="stripe-settings-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Stripe Settings
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-wt-surface rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-wt-primary/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-wt-primary" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Companies</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
          </div>
        </div>

        {Object.entries(PLAN_CONFIG).map(([key, config]) => (
          <div key={key} className="bg-wt-surface rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                <config.icon className={`w-5 h-5 ${config.textColor}`} />
              </div>
              <div>
                <p className="text-gray-400 text-sm">{config.name}</p>
                <p className="text-2xl font-bold text-white">{stats.byPlan[key] || 0}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-wt-surface border-white/10"
          />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-full md:w-48 bg-wt-surface border-white/10">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Button 
          variant="outline" 
          onClick={fetchData}
          className="border-white/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Companies Table */}
      <div className="bg-wt-surface rounded-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-sm font-medium text-gray-400">Company</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Plan</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Orders Usage</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Leads Usage</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Users</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Source</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => {
                const planType = company.plan_type || 'free';
                const planConfig = PLAN_CONFIG[planType] || PLAN_CONFIG.free;
                const PlanIcon = planConfig.icon;
                
                const ordersUsed = company.current_usage?.orders || 0;
                const ordersLimit = company.limits?.orders || 100;
                const ordersPercentage = getUsagePercentage(ordersUsed, ordersLimit);
                
                const leadsUsed = company.current_usage?.leads || 0;
                const leadsLimit = company.limits?.leads || 100;
                const leadsPercentage = getUsagePercentage(leadsUsed, leadsLimit);

                const usersUsed = company.current_usage?.users || 0;
                const usersLimit = company.limits?.users || 2;

                return (
                  <tr 
                    key={company.id} 
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`company-row-${company.id}`}
                  >
                    {/* Company Info */}
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-wt-primary/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-wt-primary" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{company.name || 'Unnamed'}</p>
                          <p className="text-gray-400 text-sm">{company.shop_domain || company.id.slice(0, 8)}</p>
                        </div>
                      </div>
                    </td>

                    {/* Plan Badge */}
                    <td className="p-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${planConfig.badgeColor}`}>
                        <PlanIcon className="w-4 h-4" />
                        <span className="font-medium">{planConfig.name}</span>
                      </div>
                    </td>

                    {/* Orders Usage */}
                    <td className="p-4">
                      <div className="w-32">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-300">{formatNumber(ordersUsed)}</span>
                          <span className="text-gray-500">/ {formatNumber(ordersLimit)}</span>
                        </div>
                        <Progress 
                          value={ordersPercentage} 
                          className={`h-1.5 ${getUsageColor(ordersPercentage)}`}
                        />
                      </div>
                    </td>

                    {/* Leads Usage */}
                    <td className="p-4">
                      <div className="w-32">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-300">{formatNumber(leadsUsed)}</span>
                          <span className="text-gray-500">/ {formatNumber(leadsLimit)}</span>
                        </div>
                        <Progress 
                          value={leadsPercentage} 
                          className={`h-1.5 ${getUsageColor(leadsPercentage)}`}
                        />
                      </div>
                    </td>

                    {/* Users */}
                    <td className="p-4">
                      <span className="text-gray-300">
                        {usersUsed} / {usersLimit}
                      </span>
                    </td>

                    {/* Source */}
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                        company.source === 'shopify_install' 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-gray-500/20 text-gray-300'
                      }`}>
                        {company.source === 'shopify_install' ? (
                          <>
                            <ShoppingBag className="w-3 h-3" />
                            Shopify
                          </>
                        ) : (
                          <>
                            <Users className="w-3 h-3" />
                            Manual
                          </>
                        )}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-wt-surface border-white/10">
                          <DropdownMenuItem 
                            onClick={() => handleEditPlan(company)}
                            className="cursor-pointer"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Change Plan
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleResetUsage(company.id)}
                            className="cursor-pointer"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reset Usage
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleViewHistory(company)}
                            className="cursor-pointer"
                          >
                            <History className="w-4 h-4 mr-2" />
                            View History
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredCompanies.length === 0 && (
          <div className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No companies found</p>
          </div>
        )}
      </div>

      {/* Edit Plan Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-wt-surface border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-wt-primary" />
              Change Plan
            </DialogTitle>
          </DialogHeader>

          {selectedCompany && (
            <div className="space-y-4">
              {/* Company Info */}
              <div className="bg-wt-bg rounded-lg p-4">
                <p className="text-white font-medium">{selectedCompany.name}</p>
                <p className="text-gray-400 text-sm">{selectedCompany.shop_domain || selectedCompany.id}</p>
              </div>

              {/* Current Plan */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Current Plan</p>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  PLAN_CONFIG[selectedCompany.plan_type || 'free']?.badgeColor
                }`}>
                  {React.createElement(
                    PLAN_CONFIG[selectedCompany.plan_type || 'free']?.icon || Sparkles,
                    { className: "w-4 h-4" }
                  )}
                  <span className="font-medium">
                    {PLAN_CONFIG[selectedCompany.plan_type || 'free']?.name}
                  </span>
                </div>
              </div>

              {/* New Plan Selection */}
              <div>
                <p className="text-sm text-gray-400 mb-2">New Plan</p>
                <Select value={newPlan} onValueChange={setNewPlan}>
                  <SelectTrigger className="bg-wt-bg border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLAN_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <config.icon className={`w-4 h-4 ${config.textColor}`} />
                          <span>{config.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Plan Details */}
              {newPlan && plans.find(p => p.id === newPlan) && (
                <div className="bg-wt-bg rounded-lg p-4 space-y-2">
                  <p className="text-sm text-gray-400">Plan Limits:</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-wt-surface rounded">
                      <p className="text-white font-medium">
                        {formatNumber(plans.find(p => p.id === newPlan)?.orders_limit || 999999)}
                      </p>
                      <p className="text-gray-400 text-xs">Orders</p>
                    </div>
                    <div className="text-center p-2 bg-wt-surface rounded">
                      <p className="text-white font-medium">
                        {formatNumber(plans.find(p => p.id === newPlan)?.leads_limit || 999999)}
                      </p>
                      <p className="text-gray-400 text-xs">Leads</p>
                    </div>
                    <div className="text-center p-2 bg-wt-surface rounded">
                      <p className="text-white font-medium">
                        {plans.find(p => p.id === newPlan)?.users_limit || 100}
                      </p>
                      <p className="text-gray-400 text-xs">Users</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reason */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Reason for Change</p>
                <Textarea
                  value={updateReason}
                  onChange={(e) => setUpdateReason(e.target.value)}
                  placeholder="e.g., Customer upgraded via external payment"
                  className="bg-wt-bg border-white/10 resize-none"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePlan}
              disabled={updating || newPlan === (selectedCompany?.plan_type || 'free')}
              className="bg-wt-primary"
            >
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Plan'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="bg-wt-surface border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-wt-primary" />
              Plan Change History
            </DialogTitle>
          </DialogHeader>

          {selectedCompany && (
            <div>
              <div className="bg-wt-bg rounded-lg p-3 mb-4">
                <p className="text-white font-medium">{selectedCompany.name}</p>
              </div>

              {logsLoading ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-8 h-8 text-wt-primary animate-spin mx-auto" />
                </div>
              ) : planLogs.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {planLogs.map((log, idx) => (
                    <div 
                      key={idx}
                      className="bg-wt-bg rounded-lg p-4 border border-white/5"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            PLAN_CONFIG[log.old_plan]?.badgeColor || 'bg-gray-500/20 text-gray-300'
                          }`}>
                            {PLAN_CONFIG[log.old_plan]?.name || log.old_plan}
                          </span>
                          <ArrowUpRight className="w-4 h-4 text-gray-500" />
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            PLAN_CONFIG[log.new_plan]?.badgeColor || 'bg-gray-500/20 text-gray-300'
                          }`}>
                            {PLAN_CONFIG[log.new_plan]?.name || log.new_plan}
                          </span>
                        </div>
                        <span className="text-gray-500 text-xs">
                          {new Date(log.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {log.reason && (
                        <p className="text-gray-400 text-sm">{log.reason}</p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        by {log.changed_by_email}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <Clock className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No plan changes recorded</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHistoryModalOpen(false)}
              className="border-white/10"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
